import { spacing } from '@/util/tailwindHelpers'
import { ReactNode } from 'react'
import AbstractProductGrid, { Card } from './AbstractProductGrid'
import { CardMetrics } from '@/components/ProductFluidContainer'
import useBreakpoints from '@/hooks/useBreakpoints'
import {
  PRODUCT_CARD_MAX_SIZE,
  PRODUCT_CARD_MIN_WIDTH,
  PRODUCT_CARD_MIN_WIDTH_MOBILE
} from '@/components/ProductCard'
import { Size } from '@/types/grid'

export const GRID_GAP = {
  regular: spacing(3),
  small: spacing(2)
}

export const GRID_X_PADDING = {
  default: spacing(4),
  mobile: spacing(2)
}

export type Layout = {
  columns: number
  columnWidth: number
  gap: number
}

export interface ProductGridItem {
  id?: number
}

export type PrelayoutMetrics = {
  containerWidth: number
  gap?: number
  maximumCardSize?: Size
  minimumCardWidth?: number
}

export interface Props<Item> {
  items?: Item[] | ((layout: Layout) => Item[])
  prelayout: (isMobile: Boolean) => PrelayoutMetrics
  itemComponent: (
    item: Item,
    layout: Layout,
    cardMetrics: CardMetrics,
    index?: number
  ) => ReactNode
  className?: string
}

const ProductGrid = <Item extends ProductGridItem>({
  items,
  itemComponent,
  prelayout,
  ...props
}: Props<Item>) => {
  const { isMobile } = useBreakpoints(['mobile'])

  const metrics = {
    containerWidth: 1200,
    gap: isMobile ? GRID_GAP.small : GRID_GAP.regular,
    maximumCardSize: PRODUCT_CARD_MAX_SIZE,
    minimumCardWidth: isMobile
      ? PRODUCT_CARD_MIN_WIDTH_MOBILE
      : PRODUCT_CARD_MIN_WIDTH,
    ...prelayout(isMobile)
  }

  // Removing this will cause the grid to jump and introduce hydration errors on /marketplace.
  // This check does however create an issue with SSR where antyhing that uses the ProductGrid
  // will not render via SSR, therefore web crawlers will not be able to see anything that
  // the ProductGrid produces. We need to fix this but it's a bigger issue because simply removing
  // this check causes the other two issues described above.
  // (ProductGrid component prevents contents from being rendered on the server side)[https://app.asana.com/0/1202825188198241/1208671706951706/f]
  // Trevor - 10/31/2024
  if (metrics.containerWidth == undefined) return null

  const cards = (layout: Layout): Card<Item>[] =>
    (typeof items === 'function' ? items(layout) : items).map(
      (item, index) =>
        ({
          index: index,
          id: item?.id || index,
          content: (layout, cardMetrics) =>
            itemComponent(item, layout, cardMetrics, index),
          details: item
        } as Card<Item>)
    )

  if (cards.length < 1) return null

  // @todo: Make a utility class that contains this logic as well as the same logic
  // contained within the inventory browser's grid layout logic.
  // Trevor - 05/08/2023

  return (
    <AbstractProductGrid
      cards={cards}
      minimumCardWidth={metrics.minimumCardWidth}
      maximumCardSize={metrics.maximumCardSize}
      layoutGenerator={{
        type: 'default',
        containerWidth: metrics.containerWidth,
        gap: metrics.gap
      }}
      {...props}
    />
  )
}

export default ProductGrid
